import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { Receipt, DollarSign, Banknote } from 'lucide-react';
import apiClient from '@/services/api';
import type { PaymentsReport } from '@/types/api';
import { KPICard } from '@/components/reports/KPICard';
import { ReportFrame, SimpleTable } from '@/components/reports/ReportFrame';
import type { ReportViewProps } from '@/components/reports/reportTypes';
import { buildGenericCsv, downloadCsv, printGenericReport, type ReportSection } from '@/utils/reportExport';

/** POS Transactions: every payment transaction in the range plus a per-type summary. */
export default function PosTransactionsReport({ filters }: ReportViewProps) {
  const { selectedRestaurantId, startDate, endDate, formatCurrency, selectedRestaurant } = filters;

  const { data, isLoading, isError, refetch } = useQuery<PaymentsReport>({
    queryKey: ['payments-report', selectedRestaurantId, startDate, endDate],
    queryFn: () => apiClient.getPaymentsReport(selectedRestaurantId!, startDate, endDate),
    enabled: !!selectedRestaurantId,
  });

  const t = data?.totals;
  const txns = data?.transactions ?? [];
  const meta = () => ({ restaurantName: selectedRestaurant?.name ?? '', startDate, endDate, formatCurrency });
  const fmtTs = (s: string | null) => (s ? format(parseISO(s), 'MMM dd HH:mm') : '—');
  const isDebit = (tt: string | null) => (tt ?? '').toUpperCase() === 'DEBIT';

  // By Type summary mirrors the POS Pos Transaction report: Type (PAYMENT_SUB_TYPE) | Count | Debit | Credit.
  const byTypeRows = () =>
    (data?.byType ?? []).map((x) => [x.paymentType, x.count, formatCurrency(x.debit), formatCurrency(x.credit)]);
  const totalsRow = () => ['TOTAL', Number(t?.count ?? 0), formatCurrency(t?.debit ?? 0), formatCurrency(t?.credit ?? 0)];

  const sections = (): ReportSection[] => [
    { heading: 'By Type', headers: ['Type', 'Count', 'Debit', 'Credit'], rows: [...byTypeRows(), totalsRow()] },
    {
      heading: 'Transactions',
      headers: ['Processed', 'Ticket', 'Type', 'Debit', 'Credit', 'Status'],
      rows: txns.map((x) => [
        fmtTs(x.processedAt), x.ticketNumber ?? '—', x.paymentType ?? '—',
        isDebit(x.transactionType) ? formatCurrency(x.amount) : '—',
        isDebit(x.transactionType) ? '—' : formatCurrency(x.amount),
        x.status ?? '—',
      ]),
    },
  ];
  const kpis = (): [string, string][] => [
    ['Transactions', Number(t?.count ?? 0).toLocaleString()],
    ['Credit', formatCurrency(t?.credit ?? 0)],
    ['Debit', formatCurrency(t?.debit ?? 0)],
  ];

  return (
    <ReportFrame
      title="POS Transactions"
      description="Line-by-line payment transactions captured from the POS."
      hasRestaurant={!!selectedRestaurantId}
      isLoading={isLoading}
      isError={isError}
      onRetry={() => refetch()}
      isEmpty={(t?.count ?? 0) === 0}
      exportDisabled={!data}
      onExportCsv={() => data && downloadCsv(`pos-transactions-${format(new Date(), 'yyyy-MM-dd')}.csv`, buildGenericCsv('POS Transactions', meta(), kpis(), sections()))}
      onExportPdf={() => data && printGenericReport('POS Transactions Report', meta(), kpis(), sections())}
    >
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <KPICard label="Transactions" value={Number(t?.count ?? 0).toLocaleString()} icon={Receipt} palette="blue" />
        <KPICard label="Credit" value={formatCurrency(t?.credit ?? 0)} icon={DollarSign} palette="green" />
        <KPICard label="Debit" value={formatCurrency(t?.debit ?? 0)} icon={Banknote} palette="purple" />
      </div>
      <div className="card"><div className="card-body">
        <h3 className="text-lg font-semibold mb-4">By Type</h3>
        <SimpleTable headers={['Type', 'Count', 'Debit', 'Credit']}
          rows={[...byTypeRows(), totalsRow()]} />
      </div></div>
      <div className="card"><div className="card-body">
        <h3 className="text-lg font-semibold mb-4">Transactions{data?.transactionsTruncated ? ' (capped at 2000)' : ''}</h3>
        <SimpleTable headers={['Processed', 'Ticket', 'Type', 'Debit', 'Credit', 'Status']}
          rows={txns.map((x) => [
            fmtTs(x.processedAt), x.ticketNumber ?? '—', x.paymentType ?? '—',
            isDebit(x.transactionType) ? formatCurrency(x.amount) : '—',
            isDebit(x.transactionType) ? '—' : formatCurrency(x.amount),
            x.status ?? '—',
          ])} />
      </div></div>
    </ReportFrame>
  );
}
